/**
 * Client-side validators for the Contact Us modal.
 *
 * Patterns mirror the Candidate Profile validators in
 * `website/src/components/profile/ProfileFormSections.tsx` (name + phone) and the email regex
 * from `website/src/components/HiringIntakeForm.tsx` — without the free-email-provider
 * blocklist, because the contact form is for anyone.
 *
 * Returns null on success, a user-facing error string on failure. Backend re-validates with
 * the same rules via Zod (see `backend/src/modules/inquiries/inquiries.dto.ts`).
 */

export function validateContactName(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Name is required';
  if (v.length < 2) return 'Name must be at least 2 characters';
  if (v.length > 100) return 'Name must be 100 characters or less';
  if (!/[a-zA-Z]/.test(v)) return 'Name must contain at least one letter';
  if (!/^[a-zA-Z\s.'\-]+$/.test(v)) return "Name can only contain letters and . ' -";
  return null;
}

export function validateContactEmail(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Email is required';
  if (v.length > 255) return 'Email is too long';
  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)) return 'Invalid email format';
  return null;
}

/** Phone is optional — empty/whitespace returns null (no error). */
export function validateContactPhone(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (v.length > 20) return 'Phone is too long';
  if (!/^[\d\s+\-()/]+$/.test(v)) return 'Phone contains invalid characters';
  const digits = v.match(/\d/g)?.length ?? 0;
  if (digits < 10) return 'Phone must contain at least 10 digits';
  return null;
}

export function validateContactMessage(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Message is required';
  if (v.length < 10) return 'Message must be at least 10 characters';
  if (v.length > 2000) return 'Message must be 2000 characters or less';
  return null;
}

export type ContactFieldKey = 'name' | 'email' | 'phone' | 'message';

export interface ContactValidationResult {
  field: ContactFieldKey;
  message: string;
}

/** Returns the first invalid field, or null if everything is valid. */
export function validateContactForm(input: {
  name: string;
  email: string;
  phone: string;
  message: string;
}): ContactValidationResult | null {
  const nameErr = validateContactName(input.name);
  if (nameErr) return { field: 'name', message: nameErr };
  const emailErr = validateContactEmail(input.email);
  if (emailErr) return { field: 'email', message: emailErr };
  const phoneErr = validateContactPhone(input.phone);
  if (phoneErr) return { field: 'phone', message: phoneErr };
  const messageErr = validateContactMessage(input.message);
  if (messageErr) return { field: 'message', message: messageErr };
  return null;
}

export function contactFieldDomId(field: ContactFieldKey): string {
  return `contact-field-${field}`;
}
